<template>
  <div class="page-shell import-record-page">
    <div class="page-header">
      <div>
        <p class="page-kicker">IMPORT TASKS</p>
        <h2 class="page-title">导入记录</h2>
        <p class="page-subtitle">查看 Excel 上传后的异步解析进度、导入数量和失败原因。</p>
      </div>
      <button class="hf-btn" type="button" :disabled="loading" @click="getList">
        <span>↻</span>
        <span>刷新</span>
      </button>
    </div>

    <section class="toolbar-panel glass-panel">
      <div class="filter-row">
        <label class="hf-form-item program-filter">
          <span class="hf-label">项目</span>
          <select class="hf-select" v-model="queryParams.programId">
            <option value="">全部项目</option>
            <option
              v-for="program in programList"
              :key="getProgramId(program)"
              :value="getProgramId(program)"
            >
              {{ program.programName || getProgramId(program) }}
            </option>
          </select>
        </label>
        <label class="hf-form-item status-filter">
          <span class="hf-label">导入状态</span>
          <select class="hf-select" v-model="queryParams.importStatus">
            <option value="">全部状态</option>
            <option v-for="item in statusOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </label>
        <div class="form-actions">
          <button class="hf-btn hf-btn--primary" type="button" :disabled="loading" @click="handleQuery">搜索</button>
          <button class="hf-btn" type="button" :disabled="loading" @click="resetQuery">重置</button>
        </div>
      </div>
    </section>

    <section class="table-panel glass-panel">
      <div class="hf-table-wrap">
        <table class="hf-table import-table">
          <thead>
            <tr>
              <th>上传时间</th>
              <th>项目</th>
              <th>导入状态</th>
              <th>导入条数</th>
              <th>完成时间</th>
              <th>Excel 文件</th>
              <th>失败原因</th>
            </tr>
          </thead>
          <tbody v-if="taskList.length">
            <tr v-for="row in taskList" :key="row.id">
              <td :data-tooltip="formatTime(row.uploadTime) || null">{{ formatTime(row.uploadTime) }}</td>
              <td>
                <div class="program-name" :data-tooltip="getProgramName(row.programId) || null">{{ getProgramName(row.programId) }}</div>
<!--                <small class="muted-text">{{ row.programId || '-' }}</small>-->
              </td>
              <td>
                <span class="hf-tag" :class="statusClassMap[row.importStatus] || ''">
                  {{ statusTextMap[row.importStatus] || row.importStatus || '-' }}
                </span>
              </td>
              <td>{{ row.importCount === undefined || row.importCount === null ? '-' : row.importCount }}</td>
              <td :data-tooltip="formatTime(row.finishTime) || null">{{ formatTime(row.finishTime) }}</td>
              <td class="path-cell" :data-tooltip="row.excelPath || null">{{ getFileName(row.excelPath) }}</td>
              <td class="reason-cell" :data-tooltip="row.failReason || null">{{ row.failReason || '-' }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!taskList.length && !loading" class="hf-table-empty">暂无导入记录</div>
        <div v-if="loading" class="hf-loading">加载中...</div>
      </div>

      <div class="pagination-wrap">
        <div class="hf-pagination">
          <button class="hf-page-btn" type="button" :disabled="current <= 1 || loading" @click="findPage(current - 1)">‹</button>
          <button
            v-for="page in pageNumbers"
            :key="page"
            class="hf-page-btn"
            :class="{ active: page === current }"
            type="button"
            :disabled="loading"
            @click="findPage(page)"
          >{{ page }}</button>
          <button class="hf-page-btn" type="button" :disabled="current >= pageCount || loading" @click="findPage(current + 1)">›</button>
          <select class="hf-select page-size" v-model.number="size" :disabled="loading" @change="findSize">
            <option v-for="item in [10, 20, 50, 100]" :key="item" :value="item">{{ item }} 条/页</option>
          </select>
          <span>共 {{ total }} 条</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { listNlProgramInfo, pageNlPromotionIncomeImportTask } from '@/api/IncomeStatic'

export default {
  name: 'IncomeImportRecords',
  data() {
    return {
      loading: false,
      taskList: [],
      programList: [],
      current: 1,
      size: 20,
      total: 0,
      queryParams: {
        programId: '',
        importStatus: ''
      },
      statusOptions: [
        { value: 'WAITING', label: '等待' },
        { value: 'PROCESSING', label: '处理中' },
        { value: 'SUCCESS', label: '成功' },
        { value: 'FAIL', label: '失败' }
      ],
      statusTextMap: {
        WAITING: '等待',
        PROCESSING: '处理中',
        SUCCESS: '成功',
        FAIL: '失败'
      },
      statusClassMap: {
        WAITING: '',
        PROCESSING: 'hf-tag--processing',
        SUCCESS: 'hf-tag--success',
        FAIL: 'hf-tag--danger'
      }
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.size))
    },
    pageNumbers() {
      const pages = []
      const start = Math.max(1, Math.min(this.current - 2, this.pageCount - 4))
      const end = Math.min(this.pageCount, start + 4)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted() {
    this.loadProgramList()
    this.getList()
  },
  methods: {
    loadProgramList() {
      listNlProgramInfo().then((res) => {
        this.programList = res.data || []
      }).catch(() => {
        this.programList = []
      })
    },
    getList() {
      this.loading = true
      pageNlPromotionIncomeImportTask(this.current, this.size, this.queryParams).then((res) => {
        const page = res.data || {}
        this.taskList = page.records || []
        this.total = Number(page.total || 0)
        this.current = Number(page.current || this.current || 1)
        this.size = Number(page.size || this.size || 20)
      }).catch((error) => {
        this.$notify({ title: '导入记录加载失败', message: error.message || '', type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery() {
      this.current = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        programId: '',
        importStatus: ''
      }
      this.handleQuery()
    },
    findPage(page) {
      if (page < 1 || page > this.pageCount || page === this.current) return
      this.current = page
      this.getList()
    },
    findSize() {
      this.current = 1
      this.getList()
    },
    getProgramId(program) {
      const value = program.programFlag
      return value !== undefined && value !== null ? String(value) : ''
    },
    getProgramName(programId) {
      const target = this.programList.find(item => this.getProgramId(item) === String(programId))
      return target ? target.programName : (programId || '-')
    },
    getFileName(path) {
      if (!path) return '-'
      const parts = String(path).split('/')
      return parts[parts.length - 1] || path
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ')
    }
  }
}
</script>

<style scoped>
.import-record-page {
  padding-bottom: 12px;
}

.filter-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 12px;
}

.program-filter {
  width: 260px;
}

.status-filter {
  width: 160px;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.import-table {
  min-width: 980px;
}

.program-name {
  color: #101828;
  font-weight: 800;
}

.path-cell,
.reason-cell {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reason-cell {
  max-width: 280px;
}

.hf-tag--processing {
  border-color: rgba(47, 128, 237, .18);
  background: rgba(235, 245, 255, .9);
  color: #175cd3;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-start;
  padding-top: 14px;
  overflow-x: auto;
}

.page-size {
  width: 110px;
}

@media (max-width: 760px) {
  .program-filter,
  .status-filter {
    width: 100%;
  }

  .form-actions {
    flex-wrap: wrap;
  }
}
</style>
